<template>
  <ControlWrapper
    v-bind="control.controlWrapper.value"
    :styles="control.styles"
    :applied-options="control.appliedOptions.value"
    :error="error || control.control.value.errors"
  >
    <h3 :class="control.styles.control.label">{{ control.computedLabel.value }}</h3>
    <div class="widget-select-button">
      <WButton
        v-for="(label, index) in labels"
        :key="String(label)"
        :disabled="!control.control.value.enabled"
        :autofocus="control.appliedOptions.value.focus"
        :data-selected="value === values[index]"
        type="outline"
        :variant="getClientTheme()"
        @click="valueChanged(values[index])"
        >{{ label }}</WButton
      >
    </div>
    <HeightTransition :visible="value === false" inner-class="mt-8">
      <div class="widget-driver">
        <div class="flex w-full flex-col gap-4">
          <h4 v-if="!contacts?.length" class="body2-bold">{{ otherContactsSection.emptyTitle }}</h4>
          <h4 v-else-if="!contacts?.[0]?.new" class="body2-bold">{{ otherContactsSection.title }}</h4>
          <WButton
            v-for="(contact, index) in contacts"
            :key="index"
            class="widget-driver_other group"
            :data-selected="!checkTheme('landrover') ? contact.selected || false : false"
            type="outline"
            :variant="getClientTheme()"
            @click="selectContact(index)"
          >
            <div class="relative flex w-full items-center justify-between">
              <div class="flex items-center gap-2">
                <div v-if="contact.new" class="flex items-center rounded-xl bg-positive-500 px-3">
                  <p class="body2 font-normal normal-case text-white">New</p>
                </div>
                <h2 class="widget-driver_other--name" :data-selected="contact.selected || false">
                  {{ contact.firstName }} {{ contact.lastName }}
                </h2>
              </div>
              <div class="absolute right-0 flex flex-row items-center gap-2">
                <div v-if="contact.selected" class="flex size-6 items-center justify-center rounded-full bg-primary">
                  <WidgetIcon :icon="themeIcons.check" class="text-background" />
                </div>
                <WButton type="ghost" @click.prevent.stop="isAddDriverModalOpen = true">
                  <WidgetIcon :icon="themeIcons.edit" height="1.5rem" class="widget-action-icon" />
                </WButton>
              </div>
            </div>

            <div v-if="contact.drivingLicenseNumber" class="flex items-center gap-x-2">
              <WidgetIcon
                :icon="themeIcons.idCard"
                class="widget-driver_other--icon"
                :data-selected="contact.selected || false"
              />
              <p class="widget-driver_other--detail" :data-selected="contact.selected || false">
                {{ contact.drivingLicenseNumber }}
              </p>
            </div>
            <div class="flex items-center gap-x-2">
              <WidgetIcon
                :icon="themeIcons.mail"
                class="widget-driver_other--icon"
                :data-selected="contact.selected || false"
              />
              <p class="widget-driver_other--detail" :data-selected="contact.selected || false">
                {{ contact.email || 'Unknown' }}
              </p>
            </div>
            <div>
              <PhoneInput
                :phone="typeof contact.phone === 'string' ? contact.phone : contact.phone?.formatted"
                hidden
                @phone-changed="(phone) => (contact.phone = phone)"
              />
              <div class="flex items-center gap-x-2">
                <WidgetIcon
                  :icon="themeIcons.phone"
                  class="widget-driver_other--icon"
                  :data-selected="contact.selected || false"
                />
                <p class="widget-driver_other--detail" :data-selected="contact.selected || false">
                  {{
                    typeof contact.phone === 'string'
                      ? contact.phone || 'Unknown'
                      : contact.phone?.formatted || 'Unknown'
                  }}
                </p>
              </div>
            </div>
            <div v-if="contact.address?.formattedAddress" class="flex items-center gap-x-2">
              <WidgetIcon
                :icon="themeIcons.marker"
                class="widget-driver_other--icon"
                :data-selected="contact.selected || false"
              />
              <p class="widget-driver_other--detail" :data-selected="contact.selected || false">
                {{ contact.address?.formattedAddress || 'Unknown' }}
              </p>
            </div>
          </WButton>
        </div>
        <div class="mt-6 self-start" v-if="!contacts?.[0]?.new">
          <WButton
            v-if="!checkTheme('default')"
            class="self-start"
            :class="{ 'text-primary': checkTheme('volvo') }"
            :type="checkTheme('common') ? 'primary' : 'outline'"
            @click="isAddDriverModalOpen = true"
          >
            {{ otherContactsSection.btnText }}
          </WButton>
          <WButton
            v-else
            class="title2-bold flex flex-nowrap items-center justify-center gap-2 px-8 text-white"
            :type="checkTheme('common') ? 'primary' : 'outline'"
            @click="isAddDriverModalOpen = true"
          >
            <WidgetIcon :icon="otherContactsSection.btnIcon" height="1.5rem" class="!transition-none"></WidgetIcon>
            <p class="title4-bold">{{ otherContactsSection.btnText }}</p>
          </WButton>
        </div>
      </div>
    </HeightTransition>
    <Modal
      v-if="isAddDriverModalOpen"
      v-model="isAddDriverModalOpen"
      id="add-driver-modal"
      :size="isMobile ? 'full' : 'lg'"
      title="Add driver details"
    >
      <template #header>
        <div>
          <h2 class="title3-bold">{{ contacts?.[0]?.new ? 'Edit driver details' : 'Add driver details' }}</h2>
        </div>
      </template>
      <!-- @vue-ex type is not correct but compatible-->
      <SimpleSection
        :contact="newDriver"
        :required="['firstName', 'lastName', 'email', 'phone.formatted']"
        :account-contact-phone="accountContactPhone"
        @error="setError"
      />
      <template #footer>
        <WButton type="primary" class="w-full" :disabled="isError" @click="addDriver"> Submit </WButton>
      </template>
    </Modal>
  </ControlWrapper>
</template>

<script setup lang="ts">
  import { ref, computed, defineAsyncComponent, inject } from 'vue';
  import { ControlElement, JsonFormsSubStates } from '@jsonforms/core';
  import { rendererProps, useJsonFormsControl } from '@jsonforms/vue';
  import { useWidgetControl } from '../util';
  import WidgetIcon from 'src/components/base/WidgetIcon.vue';
  import { config, themeIcons, checkTheme, getClientTheme } from 'src/utils/config';
  import ControlWrapper from './ControlWrapper.vue';
  import WButton from 'src/components/base/Button.vue';
  import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
  import SimpleSection from 'src/components/base/overlays/SimpleSection.vue';
  import { UserConfig } from 'src/types/widget';
  import { useWidgetError } from '../util/composition';
  import HeightTransition from 'src/components/base/HeightTransition.vue';
  import PhoneInput from 'src/components/base/PhoneInput.vue';
  import { Contact } from 'src/types/overlays';

  const Modal = defineAsyncComponent(() => import('src/components/base/Modal.vue'));

  const props = defineProps(rendererProps<ControlElement>());

  const control = useWidgetControl(useJsonFormsControl(props));
  const jsonforms = inject<JsonFormsSubStates>('jsonforms');
  const value = ref();
  const contacts = ref<UserConfig[]>(JSON.parse(JSON.stringify(config.value.insurance?.linkedContacts || [])));

  if (typeof control.control.value.data?.[0] === 'boolean') {
    value.value = control.control.value.data?.[0];
  }

  const error = ref('');
  const { errors } = useWidgetError();

  if (typeof control.control.value.data?.[0] === 'object') {
    contacts.value = control.control.value.data;
    value.value = false;
  }

  const otherContactsSection = ref(
    (
      control.control.value.schema as unknown as {
        otherContact: { title: string; emptyTitle: string; btnIcon: string; btnText: string };
      }
    ).otherContact
  );

  const breakpoints = useBreakpoints(breakpointsTailwind);
  const isMobile = breakpoints.smaller('lg');
  const isAddDriverModalOpen = ref(false);
  const newDriver = ref<Contact>(
    contacts.value?.[0]?.new
      ? (contacts.value?.[0] as Contact)
      : {
          firstName: '',
          lastName: '',
          email: '',
          phone: { formatted: '' },
          type: 'driver',
          address: {},
        }
  );
  const isError = ref(false);

  const accountContactPhone = computed(() => {
    const rootData = jsonforms?.core?.data;
    const selectedUser = rootData?.missingProperties?.selectedUser;
    if (selectedUser?.phone) {
      return typeof selectedUser.phone === 'string' ? selectedUser.phone : selectedUser.phone?.formatted || '';
    }
    return '';
  });

  function setError(error: boolean) {
    isError.value = error;
  }

  function selectContact(index: number) {
    if (!contacts.value?.length) {
      return;
    }
    contacts.value = contacts.value.map((contact) => ({
      ...contact,
      selected: false,
    }));
    contacts.value[index].selected = true;
    control.onChange(contacts.value);
    errors.value['driver'] = '';
    error.value = '';
  }

  function addDriver() {
    if (contacts.value?.[0]?.new) {
      contacts.value[0] = newDriver.value;
    } else {
      contacts.value.unshift(newDriver.value);
    }
    selectContact(0);
    isAddDriverModalOpen.value = false;
  }

  if (
    !(
      'options' in control.control.value.schema &&
      typeof control.control.value.schema.options === 'object' &&
      Array.isArray(control.control.value.schema.options)
    )
  ) {
    throw new Error('must provide options param to option control');
  }

  const options = control.control.value.schema.options as [
    { title: string; value: string | number | boolean } | string | number | boolean,
  ];

  const labels = computed(() =>
    options.map((option) => {
      if (option && typeof option === 'object' && 'title' in option && 'value' in option) {
        return option.title;
      }
      return option;
    })
  );

  const values = computed(() =>
    options.map((option) => {
      if (option && typeof option === 'object' && 'title' in option && 'value' in option) {
        return option.value;
      }
      return option;
    })
  );

  function valueChanged(val: string | number | boolean | { title: string; value: string | number | boolean }) {
    if (typeof val === 'boolean') {
      control.onChange([val]);
      value.value = val;
    } else {
      value.value = val;
      let index = contacts.value.findIndex((driver) => !!driver.selected);
      if (index === -1) {
        index = 0;
      }
      selectContact(index);
    }
  }
</script>
